/** Toast duration (ms) for success toasts so they don't block the Add button on list pages. */
export const TOAST_DURATION_SUCCESS = 2000

export interface ExtensionOption {
  id: number
  number: string
  name: string
}

export type LineExtensionOption = ExtensionOption & {
  assigned_user?: number | { id: number | string } | null
  assigned_user_id?: number | string | null
}

/** Extract the numeric user id from an extension that may carry user info in different shapes. */
export function getAssignedUserId(
  extension: LineExtensionOption,
): number | undefined {
  if (
    extension.assigned_user_id !== undefined &&
    extension.assigned_user_id !== null
  ) {
    const parsed = Number(extension.assigned_user_id)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  if (typeof extension.assigned_user === 'number') {
    return extension.assigned_user
  }
  if (
    extension.assigned_user &&
    typeof extension.assigned_user === 'object' &&
    extension.assigned_user.id !== undefined
  ) {
    const parsed = Number(extension.assigned_user.id)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

/**
 * Build a map of user id → lowest line extension number assigned to that user.
 * Used to filter Assigned User selects to only include users with at least one
 * line extension (matches legacy Admin behavior).
 */
export function buildExtensionByUserIdMap(
  extensions: readonly LineExtensionOption[],
): Map<number, number> {
  const extensionByUserId = new Map<number, number>()
  for (const ext of extensions) {
    const userId = getAssignedUserId(ext)
    if (userId == null) continue
    const extensionNum = Number(ext.number)
    if (!Number.isFinite(extensionNum)) continue
    const existing = extensionByUserId.get(userId)
    if (existing == null || extensionNum < existing) {
      extensionByUserId.set(userId, extensionNum)
    }
  }
  return extensionByUserId
}

/**
 * Filter a user list to only those who have at least one line extension assigned.
 *
 * Legacy Admin omits users without line extensions from the Assigned User select
 * on device forms (they cannot meaningfully be assigned to a device because a
 * Primary Line Extension is required). If the extensions array does not carry
 * user metadata (older API response), the original list is returned unchanged.
 *
 * Callers that already need an extension-by-user map for sorting can pass it in
 * via `extensionByUserId` to avoid building the map twice.
 */
export function filterUsersWithLineExtensions<T extends { id: number }>(
  users: readonly T[],
  extensions: readonly LineExtensionOption[],
  extensionByUserId?: Map<number, number>,
): T[] {
  const hasAssignedUserMetadata = extensions.some(
    (ext) => getAssignedUserId(ext) !== undefined,
  )
  if (!hasAssignedUserMetadata) return [...users]

  const map = extensionByUserId ?? buildExtensionByUserIdMap(extensions)
  return users.filter((user) => map.has(user.id))
}

/** Extract the numeric extension id from a field that may be an object with `id` or a plain number. */
export function resolveExtensionId(extension: unknown): number | undefined {
  if (typeof extension === 'object' && extension != null && 'id' in extension) {
    return (extension as { id: number }).id
  }
  if (typeof extension === 'number') {
    return extension
  }
  return undefined
}

/** Validate that transport and server group host selections are compatible. */
export function getTransportHostValidationError(
  transport: string,
  serverGroupHost: string,
): string | null {
  const host = serverGroupHost.trim()
  if (host === 'secondary') {
    if (!transport) {
      return 'A compatible transport with a secondary server group host must be provided.'
    }
    if (transport === 'TCP' || transport === 'TLS') {
      return `The Transport selection (${transport}) is not compatible with a secondary server group host.`
    }
  }

  if (transport === 'TCP' || transport === 'TLS') {
    if (host !== 'primary') {
      return `The Transport selection (${transport}) requires a primary server group host.`
    }
  }

  return null
}
